import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback } from "react";
import { getAdminPermissions } from "@/shared/config/adminPermissions";
import { useApi } from "@/shared/utils/request";
import { entryQueryKeys } from "./entryQueryKeys";
import { type CacheSliceData, type CacheSliceKey, cacheSlices } from "./slices";
import type { AuthSliceData } from "./slices/auth";
import { type EntriesQueryScope, entriesScopeKey } from "./slices/entries";
import type { CacheSliceConfig, CacheSliceState } from "./types";

type SliceConfig<T = unknown> = CacheSliceConfig<T>;

const CACHE_QUERY_ROOT = ["cache"] as const;
const CACHE_SLICE_STALE_MS = 5 * 60 * 1000;

const defaultAuthData: AuthSliceData = {
	userId: "",
	email: "",
	firstName: null,
	lastName: null,
	displayName: "",
	initials: "?",
	isAdmin: false,
	adminPermissions: {
		overview: false,
		billing: false,
		matters: false,
		entries: false,
		calendar: false,
		support: false,
	},
};

type CacheSliceOptions = {
	scope?: EntriesQueryScope;
	enabled?: boolean;
};

function cacheSliceQueryKey(
	key: CacheSliceKey,
	userId?: string,
	scopeKey?: string,
) {
	if (key === "entries") {
		return entryQueryKeys.entryList(userId, scopeKey ?? "");
	}
	return [...CACHE_QUERY_ROOT, key, userId ?? "anonymous"] as const;
}

function authDataFromUser(
	user: ReturnType<typeof useAuth>["user"],
): AuthSliceData {
	if (!user) return defaultAuthData;

	const email = user.email ?? "";
	const { isAdmin, canView } = getAdminPermissions(email);
	return {
		userId: user.id,
		email,
		firstName: user.firstName ?? null,
		lastName: user.lastName ?? null,
		displayName:
			user.firstName && user.lastName
				? `${user.firstName} ${user.lastName}`
				: email,
		initials:
			user.firstName && user.lastName
				? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
				: (email[0]?.toUpperCase() ?? "?"),
		isAdmin,
		adminPermissions: canView,
	};
}

/** Read a single shared slice through React Query. */
export function useCacheSlice<K extends CacheSliceKey>(
	key: K,
	options: CacheSliceOptions = {},
): CacheSliceState<CacheSliceData<K>> {
	const { user, isLoading: isAuthLoading } = useAuth();
	const api = useApi();
	const config = cacheSlices[key] as SliceConfig<CacheSliceData<K>>;
	const userId = user?.id;
	const scope = key === "entries" ? options.scope : undefined;

	const query = useQuery({
		queryKey: cacheSliceQueryKey(
			key,
			userId,
			key === "entries" ? entriesScopeKey(scope) : undefined,
		),
		queryFn: () => config.fetch(api, scope),
		enabled: Boolean(userId) && key !== "auth" && options.enabled !== false,
		staleTime: CACHE_SLICE_STALE_MS,
		refetchOnWindowFocus: false,
	});

	if (key === "auth") {
		return {
			data: authDataFromUser(user) as CacheSliceData<K>,
			isLoading: isAuthLoading,
			error: null,
			lastFetchedAt: user ? Date.now() : 0,
		};
	}

	return {
		data: (query.data ?? config.initialValue) as CacheSliceData<K>,
		isLoading: query.isLoading && query.data === undefined,
		error:
			query.error instanceof Error
				? query.error.message
				: query.error
					? "Fetch failed"
					: null,
		lastFetchedAt: query.dataUpdatedAt,
	};
}

/** Access mutation helpers. */
export function useCacheMutations() {
	const { user } = useAuth();
	const api = useApi();
	const queryClient = useQueryClient();
	const userId = user?.id;

	const refresh = useCallback(
		async (key: CacheSliceKey) => {
			if (!userId || key === "auth") return;
			const config = cacheSlices[key] as SliceConfig;
			if (key === "entries") {
				await queryClient.invalidateQueries({
					queryKey: entryQueryKeys.user(userId),
					refetchType: "active",
				});
				return;
			}
			await queryClient.fetchQuery({
				queryKey: cacheSliceQueryKey(key, userId),
				queryFn: () => config.fetch(api),
				staleTime: 0,
			});
		},
		[api, queryClient, userId],
	);

	const refreshAll = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: CACHE_QUERY_ROOT,
				refetchType: "active",
			}),
			queryClient.invalidateQueries({
				queryKey: entryQueryKeys.root,
				refetchType: "active",
			}),
		]);
	}, [queryClient]);

	const optimisticUpdate = useCallback(
		<K extends CacheSliceKey>(
			key: K,
			updater: (prev: CacheSliceData<K>) => CacheSliceData<K>,
		): (() => void) => {
			if (key === "entries") {
				const prefix = entryQueryKeys.entries(userId);
				const previousQueries = queryClient.getQueriesData<CacheSliceData<K>>({
					queryKey: prefix,
				});
				for (const [queryKey, previous] of previousQueries) {
					const current =
						previous ?? (cacheSlices[key].initialValue as CacheSliceData<K>);
					queryClient.setQueryData<CacheSliceData<K>>(
						queryKey,
						updater(current),
					);
				}
				return () => {
					for (const [queryKey, previous] of previousQueries) {
						queryClient.setQueryData<CacheSliceData<K>>(
							queryKey,
							previous ?? (cacheSlices[key].initialValue as CacheSliceData<K>),
						);
					}
				};
			}

			const queryKey = cacheSliceQueryKey(key, userId);
			const previous = queryClient.getQueryData<CacheSliceData<K>>(queryKey);
			const current =
				previous ?? (cacheSlices[key].initialValue as CacheSliceData<K>);

			queryClient.setQueryData<CacheSliceData<K>>(queryKey, updater(current));

			return () => {
				queryClient.setQueryData<CacheSliceData<K>>(
					queryKey,
					previous ?? current,
				);
			};
		},
		[queryClient, userId],
	);

	return { refresh, refreshAll, optimisticUpdate };
}
